import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Trash2, ExternalLink, Plus, CheckCircle2, XCircle } from "lucide-react"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { FinancialCharge, useUpdateFinancialCharge, useDeleteFinancialCharge } from "@/hooks/useFinancialCharges"
import { usePaymentReceipts, useCreatePaymentReceipt, useDeletePaymentReceipt } from "@/hooks/usePaymentReceipts"
import { formatCurrency } from "@/lib/utils"
import { useAuth } from "@/hooks/useAuth"
import { toast } from "sonner"

interface DetalhesCobrancaModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  charge: FinancialCharge | null
}

export const DetalhesCobrancaModal = ({ open, onOpenChange, charge }: DetalhesCobrancaModalProps) => {
  const { profile } = useAuth()
  const { data: receipts = [] } = usePaymentReceipts(charge?.id || "")
  const updateCharge = useUpdateFinancialCharge()
  const deleteCharge = useDeleteFinancialCharge()
  const createReceipt = useCreatePaymentReceipt()
  const deleteReceipt = useDeletePaymentReceipt()

  const [newReceipt, setNewReceipt] = useState({
    amount_cents: 0,
    method: "",
    transaction_id: "",
    paid_at: format(new Date(), "yyyy-MM-dd'T'HH:mm")
  })

  if (!charge) return null

  const getStatusBadge = (status: string) => {
    const variants = {
      paid: { label: "Pago", className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" },
      pending: { label: "Pendente", className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100" },
      overdue: { label: "Vencida", className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100" },
      canceled: { label: "Cancelada", className: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100" }
    }
    const variant = variants[status as keyof typeof variants] || variants.pending
    return <Badge className={variant.className}>{variant.label}</Badge>
  }

  const totalPaid = receipts.reduce((sum, r) => sum + r.amount_cents, 0)
  const remainingAmount = charge.amount_cents - totalPaid

  const handleStatusChange = async (newStatus: string) => {
    await updateCharge.mutateAsync({
      id: charge.id,
      updates: { status: newStatus as any }
    })
  }

  const handleDeleteCharge = async () => {
    if (confirm("Tem certeza que deseja deletar esta cobrança?")) {
      await deleteCharge.mutateAsync(charge.id)
      onOpenChange(false)
    }
  }

  const handleAddReceipt = async () => {
    if (!newReceipt.method || newReceipt.amount_cents <= 0) {
      toast.error("Preencha todos os campos obrigatórios")
      return
    }

    await createReceipt.mutateAsync({
      charge_id: charge.id,
      amount_cents: newReceipt.amount_cents,
      method: newReceipt.method,
      transaction_id: newReceipt.transaction_id || undefined,
      paid_at: newReceipt.paid_at,
      recorded_by: profile?.id
    })

    setNewReceipt({
      amount_cents: 0,
      method: "",
      transaction_id: "",
      paid_at: format(new Date(), "yyyy-MM-dd'T'HH:mm")
    })
  }

  const handleDeleteReceipt = async (receiptId: string) => {
    if (confirm("Tem certeza que deseja remover este recibo?")) {
      await deleteReceipt.mutateAsync({ id: receiptId, chargeId: charge.id })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Detalhes da Cobrança</span>
            {getStatusBadge(charge.status)}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="info" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="info">Informações</TabsTrigger>
            <TabsTrigger value="payments">Pagamentos ({receipts.length})</TabsTrigger>
            <TabsTrigger value="actions">Ações</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Dados da Cobrança</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-muted-foreground">Descrição</Label>
                  <p className="font-medium">{charge.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Valor</Label>
                    <p className="text-2xl font-bold text-primary">{formatCurrency(charge.amount_cents)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Vencimento</Label>
                    <p className="font-medium">
                      {format(new Date(charge.due_date), "dd/MM/yyyy", { locale: ptBR })}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Unidade</Label>
                    <p className="font-medium">
                      {charge.unit?.building && `${charge.unit.building} - `}Unidade {charge.unit?.number}
                    </p>
                  </div>
                  {charge.resident && (
                    <div>
                      <Label className="text-muted-foreground">Morador</Label>
                      <p className="font-medium">{charge.resident.full_name}</p>
                    </div>
                  )}
                </div>
                {charge.payment_method && (
                  <div>
                    <Label className="text-muted-foreground">Método de Pagamento</Label>
                    <p className="font-medium capitalize">{charge.payment_method}</p>
                  </div>
                )}
                {charge.external_payment_link && (
                  <div>
                    <Label className="text-muted-foreground">Link de Pagamento</Label>
                    <a 
                      href={charge.external_payment_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-primary hover:underline"
                    >
                      Acessar link externo <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                )}
                {charge.notes && (
                  <div>
                    <Label className="text-muted-foreground">Observações</Label>
                    <p className="text-sm">{charge.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Pagamentos</CardTitle>
                <CardDescription>
                  Total pago: <span className="font-bold text-green-600">{formatCurrency(totalPaid)}</span>
                  {remainingAmount > 0 && (
                    <> | Restante: <span className="font-bold text-orange-600">{formatCurrency(remainingAmount)}</span></>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {receipts.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Método</TableHead>
                        <TableHead>Transação</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {receipts.map((receipt) => (
                        <TableRow key={receipt.id}>
                          <TableCell>
                            {format(new Date(receipt.paid_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                          </TableCell>
                          <TableCell className="font-medium text-green-600">
                            {formatCurrency(receipt.amount_cents)}
                          </TableCell>
                          <TableCell className="capitalize">{receipt.method}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {receipt.transaction_id || "-"}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteReceipt(receipt.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center text-muted-foreground py-4">Nenhum pagamento registrado</p>
                )}

                <div className="mt-6 space-y-3 p-4 border rounded-lg">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Plus className="h-4 w-4" /> Adicionar Pagamento
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Valor (R$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newReceipt.amount_cents ? (newReceipt.amount_cents / 100).toFixed(2) : ""}
                        onChange={(e) => setNewReceipt({ ...newReceipt, amount_cents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                      />
                    </div>
                    <div>
                      <Label>Método</Label>
                      <Select value={newReceipt.method} onValueChange={(value) => setNewReceipt({ ...newReceipt, method: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dinheiro">Dinheiro</SelectItem>
                          <SelectItem value="pix">PIX</SelectItem>
                          <SelectItem value="ted">TED</SelectItem>
                          <SelectItem value="boleto">Boleto</SelectItem>
                          <SelectItem value="cartao">Cartão</SelectItem>
                          <SelectItem value="cheque">Cheque</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>ID Transação (opcional)</Label>
                      <Input
                        value={newReceipt.transaction_id}
                        onChange={(e) => setNewReceipt({ ...newReceipt, transaction_id: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Data/Hora Pagamento</Label>
                      <Input
                        type="datetime-local"
                        value={newReceipt.paid_at}
                        onChange={(e) => setNewReceipt({ ...newReceipt, paid_at: e.target.value })}
                      />
                    </div>
                  </div>
                  <Button onClick={handleAddReceipt} disabled={createReceipt.isPending} className="w-full">
                    {createReceipt.isPending ? "Adicionando..." : "Adicionar Pagamento"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="actions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Gerenciar Cobrança</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Alterar Status</Label>
                  <Select value={charge.status} onValueChange={handleStatusChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">
                        <span className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-yellow-500" /> Pendente
                        </span>
                      </SelectItem>
                      <SelectItem value="paid">
                        <span className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" /> Pago
                        </span>
                      </SelectItem>
                      <SelectItem value="overdue">
                        <span className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-500" /> Vencida
                        </span>
                      </SelectItem>
                      <SelectItem value="canceled">
                        <span className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-gray-500" /> Cancelada
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-4 border-t">
                  <Button
                    variant="destructive"
                    onClick={handleDeleteCharge}
                    disabled={deleteCharge.isPending}
                    className="w-full"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {deleteCharge.isPending ? "Deletando..." : "Deletar Cobrança"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
